import {
    selectActiveWorkspace,
    selectActiveWorkspaceId,
    setIdRenaming,
    setIsCreatingProject,
    setNewestProjectId,
    selectTags,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { getLocalStorageValue } from "tools/store/localStorageRegistry"
import { selectPDFSettings } from "plugins/PDFv2/PDFSlice"
import { uploadPDFLogoToS3 } from "plugins/shared/uploadPDFLogoToS3"
import { RootState } from "integrations/redux"
import { selectActivatedFireId } from "features/map/control/selectors"
import { v4 } from "uuid"
import { createAppAsyncThunk } from "integrations/redux/hooks"
import { selectFireById } from "./selectors"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { ProjectExtended, TagExtended } from "@nova/schemas"
import { ContentViewMode, SupportedRegions } from "@nova/constants"
import { setProjectLocationLocally } from "./slice"
import { mutate } from "swr"

export const createFire = createAppAsyncThunk(
    "fires/createOne",
    async (
        payload: { name: string; tagId?: string; preventAutoRenameFocus?: boolean },
        thunkApi
    ) => {
        const dispatch = thunkApi.dispatch
        const state = thunkApi.getState()
        const orgId = selectActiveWorkspaceId()(state)!

        const viewMode = getLocalStorageValue("contentViewMode")

        dispatch(setIsCreatingProject(true))

        const { name, tagId, preventAutoRenameFocus } = payload

        const { project } = await fetchApiGateway("post /org/:orgId/projects", {
            params: { orgId },
            body: { name, folderId: tagId },
        })

        dispatch(setIsCreatingProject(false))
        if (!preventAutoRenameFocus) {
            dispatch(
                setIdRenaming(
                    `${viewMode === ContentViewMode.Grid ? "grid" : "list"}_${project.id}`
                )
            )
            dispatch(setNewestProjectId(project.id))
        }
        return project
    }
)
export const updateFire = createAppAsyncThunk(
    "fires/updateOne",
    async (
        payload: {
            id: string
            updates: {
                name?: string
                tagId?: string
                lastJobFlightId?: string
                thumbnailUrl?: string
                thumbnailViewstate?: string
            }
        },
        thunkApi
    ) => {
        const state = thunkApi.getState()
        const orgId = selectActiveWorkspaceId()(state)!

        const result = await fetchApiGateway("patch /org/:orgId/project/:projectId", {
            params: { orgId, projectId: payload.id },
            body: payload.updates,
        })

        return result
    }
)

export const createPDFTemplate = async (state: RootState) => {
    const organizationId = selectActiveWorkspaceId()(state)
    const projectId = selectActivatedFireId(state)
    const pdfSettings = selectPDFSettings()(state)
    const pdfTemplate = pdfSettings.template
    const pdfTemplateId = v4()

    if (organizationId && projectId && pdfTemplate) {
        // Upload left logo if it exists.
        if (pdfSettings.logoUrls.left && pdfTemplate.logos?.left) {
            await uploadPDFLogoToS3(
                organizationId,
                pdfTemplate.logos.left.id,
                pdfSettings.logoUrls.left
            )
        }

        // Upload right logo if it exists
        if (pdfSettings.logoUrls.right && pdfTemplate.logos?.right) {
            await uploadPDFLogoToS3(
                organizationId,
                pdfTemplate.logos.right.id,
                pdfSettings.logoUrls.right
            )
        }
    }

    return { pdfTemplate, pdfTemplateId }
}

export const deleteFire = createAppAsyncThunk(
    "fires/deleteOne",
    async (payload: string, thunkApi) => {
        const id = payload
        const state = thunkApi.getState()
        const orgId = selectActiveWorkspaceId()(state)!
        await fetchApiGateway("delete /org/:orgId/project/:projectId", {
            params: { orgId, projectId: id },
        })

        return { id }
    }
)

export const initializeFires = createAppAsyncThunk(
    "fires/list",
    async (payload: { organizationId: string }, { getState }) => {
        const state = getState()
        const org = selectActiveWorkspace()(state)!
        const tags = selectTags(state)

        const flatten = (tags: TagExtended[]): ProjectExtended[] =>
            tags.flatMap((tag) => [...tag.projects, ...flatten(tag.subTags)])

        return [...org.rootLevelProjects, ...flatten(tags)]
    }
)

export const overrideProjectLocation = createAppAsyncThunk(
    "fires/overrideLocation",
    async (
        payload: { projectId: string; location: { latitude: number; longitude: number } },
        thunkApi
    ) => {
        const state = thunkApi.getState()
        const orgId = selectActiveWorkspaceId()(state)!

        await fetchApiGateway("patch /org/:orgId/project/:projectId/location", {
            params: { orgId, projectId: payload.projectId },
            body: { location: payload.location, replace: true },
        })

        thunkApi.dispatch(setProjectLocationLocally(payload))
        void mutate(["geolocated-projects", orgId])

        return payload
    }
)

export const getOneProject = createAppAsyncThunk<
    ProjectExtended,
    { orgId: string; projectId: string }
>("fires/getProject", async (payload, thunkApi) => {
    const state = thunkApi.getState()
    if (selectFireById(payload.projectId)(state)) {
        return selectFireById(payload.projectId)(state)!
    }

    const project = await fetchApiGateway("get /org/:orgId/project/:projectId", {
        params: payload,
        region: SupportedRegions.Canada, // Force to Canada because it needs to access Canadian redis. Projects are pulled from dynamo based on org.region
    })
    return project
})
